import React, { useState, useContext } from "react";

import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  ContentCopy,
  Delete,
  EmojiEmotions,
  Info,
  PushPin,
  Reply,
  Share,
  StarBorder,
} from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl }) => {
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleReply = () => {
    setReplyingMessage(message);
    handleClose();
  };

  const handleCopy = () => {
    if (message.body) {
      navigator.clipboard.writeText(message.body).catch(() => {
        const el = document.createElement("textarea");
        el.value = message.body;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      });
    }
    handleClose();
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleClose();
  };

  const menuItems = [
    ...(message.fromMe
      ? [
          {
            key: "messageInfo",
            icon: <Info fontSize="small" />,
            label: i18n.t("messageOptionsMenu.messageInfo"),
            onClick: handleClose,
          },
        ]
      : []),
    {
      key: "reply",
      icon: <Reply fontSize="small" />,
      label: i18n.t("messageOptionsMenu.reply"),
      onClick: handleReply,
    },
    {
      key: "copy",
      icon: <ContentCopy fontSize="small" />,
      label: i18n.t("messageOptionsMenu.copy"),
      onClick: handleCopy,
    },
    {
      key: "react",
      icon: <EmojiEmotions fontSize="small" />,
      label: i18n.t("messageOptionsMenu.react"),
      onClick: handleClose,
    },
    {
      key: "forward",
      icon: <Share fontSize="small" />,
      label: i18n.t("messageOptionsMenu.forward"),
      onClick: handleClose,
    },
    {
      key: "pin",
      icon: <PushPin fontSize="small" />,
      label: i18n.t("messageOptionsMenu.pin"),
      onClick: handleClose,
    },
    {
      key: "star",
      icon: <StarBorder fontSize="small" />,
      label: i18n.t("messageOptionsMenu.star"),
      onClick: handleClose,
    },
    { key: "divider" },
    {
      key: "delete",
      icon: <Delete fontSize="small" color="error" />,
      label: i18n.t("messageOptionsMenu.delete"),
      onClick: handleOpenConfirmationModal,
      color: "error.main",
    },
  ];

  return (
    <>
      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={menuOpen}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {menuItems.map((item) =>
          item.key === "divider" ? (
            <Divider key="divider" />
          ) : (
            <MenuItem key={item.key} dense onClick={item.onClick}>
              <ListItemIcon sx={{ color: item.color ?? "inherit" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    variant: "body2",
                    color: item.color ?? "text.primary",
                  },
                }}
              />
            </MenuItem>
          )
        )}
      </Menu>
    </>
  );
};

export default MessageOptionsMenu;
